import moment from "moment";
import { IAbsenceComplete } from "../shared/interfaces/absences.interface";
import { plageHoraire } from "./absencesHelper";

/**
 * Helper pour gérer la logique des dates désactivées dans le calendrier
 */
export class CalendarDisabledDaysHelper {
    /**
     * Détermine le type de blocage pour une date dans une absence
     * @param date - La date à vérifier
     * @param absence - L'absence à vérifier
     * @returns Le type de blocage : 'full', 'morning', 'afternoon', ou null
     */
    private static getAbsenceBlockType(
        date: moment.Moment,
        absence: IAbsenceComplete
    ): "full" | "morning" | "afternoon" | null {
        const absenceStart = moment(absence.dateDebut);
        const absenceEnd = moment(absence.dateFin);

        if (!date.isBetween(absenceStart, absenceEnd, "day", "[]")) {
            return null;
        }

        const isStartDay = date.isSame(absenceStart, "day");
        const isEndDay = date.isSame(absenceEnd, "day");
        const isSingleDay = isStartDay && isEndDay;

        // Jour unique
        if (isSingleDay) {
            if (absence.plageDebut === plageHoraire.matin && absence.plageFin === plageHoraire.apresMidi) {
                return "full";
            }
            if (absence.plageDebut === plageHoraire.matin && absence.plageFin === plageHoraire.matin) {
                return "morning";
            }
            if (absence.plageDebut === plageHoraire.apresMidi && absence.plageFin === plageHoraire.apresMidi) {
                return "afternoon";
            }
        } else if (isStartDay) {
            if (absence.plageDebut === plageHoraire.matin) {
                return "full";
            }
            if (absence.plageDebut === plageHoraire.apresMidi) {
                return "afternoon";
            }

            return "full";
        } else if (isEndDay) {
            if (absence.plageFin === plageHoraire.apresMidi) {
                return "full";
            }
            if (absence.plageFin === plageHoraire.matin) {
                return "morning";
            }

            return "full";
        } else {
            // Jour intermédiaire
            return "full";
        }

        return null;
    }

    /**
     * Détermine la classe CSS pour une date basée sur les absences complètes
     * @param date - La date à vérifier
     * @param listAbsencesCompletes - Liste des absences complètes
     * @returns La classe CSS à appliquer, ou null si aucune
     */
    public static getAbsenceClassForDate(date: moment.Moment, listAbsencesCompletes: IAbsenceComplete[]): string | null {
        // Trouver la première absence qui bloque la date
        const matchingAbsence = listAbsencesCompletes.filter(absence => {
            const blockType = this.getAbsenceBlockType(date, absence);

            return blockType !== null;
        });

        // Si aucune absence ne bloque la date, retourner null
        if (matchingAbsence.length === 0) {
            return null;
        }

        if (matchingAbsence.length > 1) {
            return "date-entiere-desactivee";
        }

        if (matchingAbsence.length === 1) {
            const blockType = this.getAbsenceBlockType(date, matchingAbsence[0]);

            if (blockType === "full") {
                return "date-entiere-desactivee";
            }
            if (blockType === "morning") {
                return "date-matin-desactive";
            }
            if (blockType === "afternoon") {
                return "date-aprem-desactive";
            }
        }

        return null;
    }

    /**
     * Applique une classe CSS personnalisée aux cellules du calendrier
     * @param time - Timestamp de la date
     * @param listAbsencesCompletes - Liste des absences complètes
     * @param storeVariableListeJoursFeries - Liste des jours fériés
     * @returns Classe CSS à appliquer
     */
    public static cellClassName(time: number, listAbsencesCompletes: IAbsenceComplete[], storeVariableListeJoursFeries: any[]): string {
        const date = moment(time);
        const classes: string[] = [];

        // Vérifier si c'est un jour férié (entier ou partiel)
        const jourFerie = storeVariableListeJoursFeries.find(jourFerie =>
            date.isSame(moment(jourFerie.Date), "day")
        );

        if (jourFerie) {
            classes.push("jour-ferie");
        }

        // Ajouter la classe d'absence si applicable
        const absenceClass = this.getAbsenceClassForDate(date, listAbsencesCompletes);
        if (absenceClass) {
            classes.push(absenceClass);
        }

        return classes.join(" ");
    }

    /**
     * Vérifie si une date avec une plage horaire spécifique est désactivée
     * @param date - La date à vérifier
     * @param plageHoraireParam - La plage horaire (plageHoraire.matin ou plageHoraire.apresMidi)
     * @param listAbsencesCompletes - Liste des absences complètes
     * @returns true si la date+plage est désactivée, false sinon
     */
    public static isDateWithPlageDisabled(date: Date, plageHoraireParam: string, listAbsencesCompletes: IAbsenceComplete[]): boolean {
        const dateMoment = moment(date);

        // Vérifier si la date est dans une absence et si la plage horaire est compatible
        return listAbsencesCompletes.some(absence => {
            const blockType = this.getAbsenceBlockType(dateMoment, absence);
            if (!blockType) {
                return false;
            }

            if (blockType === "full") {
                return true;
            }
            if (blockType === "morning") {
                return plageHoraireParam === plageHoraire.matin;
            }
            if (blockType === "afternoon") {
                return plageHoraireParam === plageHoraire.apresMidi;
            }

            return false;
        });
    }

    /**
     * Ajuste automatiquement les plages horaires en fonction des dates désactivées
     * @param selectedDatesConge - Les dates sélectionnées
     * @param plageDateDebut - La plage horaire de début
     * @param plageDateFin - La plage horaire de fin
     * @param listAbsencesCompletes - Liste des absences complètes
     * @param plageHoraireParam - La plage horaire
     * @returns Les plages horaires ajustées
     */
    public static adjustPlageHoraires(
        selectedDatesConge: Date[] | null,
        plageDateDebut: string,
        plageDateFin: string,
        listAbsencesCompletes: IAbsenceComplete[],
        plageHoraireParam: { matin: string, apresMidi: string }
    ): { plageDateDebut: string, plageDateFin: string } {
        if (selectedDatesConge == null) {
            return { plageDateDebut, plageDateFin };
        }

        const dateDebut = selectedDatesConge[0];
        const dateFin = selectedDatesConge[1];

        const adjustPlage = (date: Date, currentPlage: string): string => {
            if (!this.isDateWithPlageDisabled(date, currentPlage, listAbsencesCompletes)) {
                return currentPlage;
            }

            const alternativePlage = currentPlage === plageHoraireParam.matin ? plageHoraireParam.apresMidi : plageHoraireParam.matin;

            return this.isDateWithPlageDisabled(date, alternativePlage, listAbsencesCompletes) ? currentPlage : alternativePlage;
        };

        return {
            plageDateDebut: adjustPlage(dateDebut, plageDateDebut),
            plageDateFin: adjustPlage(dateFin, plageDateFin)
        };
    }

    /**
     * Vérifie si une absence chevauche la période sélectionnée
     * @param dateDebut - Date de début
     * @param dateFin - Date de fin
     * @param listAbsencesCompletes - Liste des absences complètes
     * @returns true si l'absence chevauche la période sélectionnée, false sinon
     */
    public static checkChevauchementDates(dateDebut: Date, dateFin: Date, listAbsencesCompletes: IAbsenceComplete[]): IAbsenceComplete[] {
        const selectionStart = moment(dateDebut);
        const selectionEnd = moment(dateFin);

        return listAbsencesCompletes.filter(absence => {
            const absenceStart = moment(absence.dateDebut);
            const absenceEnd = moment(absence.dateFin);

            // Garder l'absence si elle chevauche la sélection (pas avant ni après)
            return !selectionEnd.isBefore(absenceStart, "day") && !selectionStart.isAfter(absenceEnd, "day");
        });
    }

    /**
     * Vérifie si une période sélectionnée chevauche une absence existante
     * @param dateDebut - Date de début
     * @param dateFin - Date de fin
     * @param plageDateDebut - Plage horaire de début
     * @param plageDateFin - Plage horaire de fin
     * @param relevantAbsences - Liste des absences existantes
     * @returns true si il y a un chevauchement avec une absence existante, false sinon
     */
    public static checkDateRangeHasExistingAbsence(
        dateDebut: Date,
        dateFin: Date,
        plageDateDebut: string,
        plageDateFin: string,
        relevantAbsences: IAbsenceComplete[]
    ): boolean {
        const selectionStart = moment(dateDebut);
        const selectionEnd = moment(dateFin);

        // Convertit une date+plage en nombre pour comparaison (jour * 2 + plage)
        // Ex: 17/10 M = 17*2+0 = 34, 17/10 A = 17*2+1 = 35, 18/10 M = 18*2+0 = 36
        const toComparable = (date: moment.Moment, plage: string): number => {
            const daysSinceEpoch = date.startOf("day").diff(moment(0), "days");

            return daysSinceEpoch * 2 + (plage === plageHoraire.apresMidi ? 1 : 0);
        };

        // Calcule le début et la fin de la sélection
        const selStartNum = toComparable(selectionStart, plageDateDebut);
        const selEndNum = toComparable(selectionEnd, plageDateFin);

        // Vérifie si au moins une absence chevauche
        return relevantAbsences.some(absence => {
            const absStartNum = toComparable(moment(absence.dateDebut), absence.plageDebut);
            const absEndNum = toComparable(moment(absence.dateFin), absence.plageFin);

            // Deux périodes se chevauchent si elles partagent au moins une demi-journée
            return selStartNum <= absEndNum && absStartNum <= selEndNum;
        });
    }
}



// WEBPACK FOOTER //
// ./src/Helper/calendarDisabledDaysHelper.ts